import { useRef, type KeyboardEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { MethodChip, Notice, StatusPill } from "../../design/components";
import { METHODS } from "../../design/methods";
import { HEALTH_PILL, METHOD_BY_CONNECTION, STATUS_LABEL } from "../../domain/health";
import { useEntity } from "../../api/queries";
import { ApiError } from "../../api/client";
import type { Entity } from "../../api/types";
import { ConnectionTab } from "./tabs/ConnectionTab";
import { OverviewTab } from "./tabs/OverviewTab";
import { MappingTab } from "./tabs/MappingTab";
import { ParticipantTab } from "./tabs/ParticipantTab";
import { UsersTable } from "../users/UsersTable";
import styles from "./CompanyDetail.module.css";

type TabId = "overview" | "connection" | "mapping" | "users" | "participant";

const TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "connection", label: "Connection" },
  { id: "mapping", label: "Mapping" },
  { id: "users", label: "Users" },
  { id: "participant", label: "Participant" },
];

function readTab(value: string | null): TabId {
  const found = TABS.find((tab) => tab.id === value);
  return found ? found.id : "overview";
}

function TabPanel({ entity, tab }: { entity: Entity; tab: (typeof TABS)[number] }) {
  if (tab.id === "connection") return <ConnectionTab entity={entity} />;
  if (tab.id === "mapping") return <MappingTab entity={entity} />;
  if (tab.id === "users") return <UsersTable variant="tab" entityId={entity.id} />;
  return tab.id === "participant" ? <ParticipantTab entity={entity} /> : <OverviewTab entity={entity} />;
}

export function CompanyDetail() {
  const { t } = useTranslation();
  const { entityId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const entity = useEntity(entityId);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const active = readTab(searchParams.get("tab"));
  const activeIndex = TABS.findIndex((tab) => tab.id === active);

  function select(id: TabId) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const last = TABS.length - 1;
    let next = -1;
    if (event.key === "ArrowRight") next = activeIndex === last ? 0 : activeIndex + 1;
    else if (event.key === "ArrowLeft") next = activeIndex === 0 ? last : activeIndex - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next < 0) return;
    event.preventDefault();
    select(TABS[next]!.id);
    tabRefs.current[next]?.focus();
  }

  if (entity.error instanceof ApiError && entity.error.status === 404) {
    return (
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{t("companies.detail.notFoundTitle", "This company is not available")}</h1>
          <p className={styles.muted}>
            <Link className="link" to="/companies">
              {t("companies.detail.backToCompanies", "Back to companies")}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (entity.isError) {
    return <Notice kind="fail">{t("companies.detail.loadError", "This company could not be loaded.")}</Notice>;
  }

  if (!entity.data) {
    return <p className={styles.muted}>{t("companies.detail.loading", "Loading…")}</p>;
  }

  const company = entity.data;
  const method = METHOD_BY_CONNECTION[company.connectionMethod];

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{company.legalName}</h1>
          <div className={styles.headMeta}>
            <span className="mono">{company.code}</span>
            <StatusPill status={company.status} label={STATUS_LABEL[company.status]} />
            <StatusPill {...HEALTH_PILL[company.health]} />
            <span title={METHODS[method].full}>
              <MethodChip method={method} />
            </span>
            {company.deployMode === "dedicated" ? (
              <span className="chip">{t("companies.detail.dedicatedInstance", "Dedicated instance")}</span>
            ) : null}
          </div>
        </div>
      </div>

      <div
        className={styles.tabs}
        role="tablist"
        aria-label={t("companies.detail.sectionsAriaLabel", "Company sections")}
        onKeyDown={onKeyDown}
      >
        {TABS.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-controls={`panel-${tab.id}`}
            aria-selected={tab.id === active}
            tabIndex={tab.id === active ? 0 : -1}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            className={`${styles.tab}${tab.id === active ? ` ${styles.tabOn}` : ""}`}
            onClick={() => select(tab.id)}
          >
            {t(`companies.detail.tabs.${tab.id}`, tab.label)}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`panel-${active}`} aria-labelledby={`tab-${active}`} tabIndex={0}>
        <TabPanel key={company.id} entity={company} tab={TABS[activeIndex]!} />
      </div>
    </>
  );
}
